import { useState, Fragment, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { formatPhoneNumber } from "@/lib/formatPhone";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Search, Users, UserX, UserCheck, ChevronDown, ChevronUp, CheckCircle, XCircle, Download, CheckCheck, ShieldAlert } from "lucide-react";
import ExcelJS from "exceljs";
import ReportsTab from "@/components/admin/ReportsTab";

type StudentInfo = {
  user_id: string; full_name: string; email: string; phone: string; created_at: string;
  progress_percent: number; completed_lessons: number; total_lessons: number;
  enrollments: { id: string; course_id: string; course_title: string; status: string }[];
  progress: { lesson_id: string; lesson_title: string; course_id: string; exam_passed: boolean; best_score: number; video_completed: boolean }[];
};

type Props = {
  sessionToken: string;
  students: StudentInfo[];
  studentsLoading: boolean;
  fetchStudents: () => void;
  courses: { id: string; title: string }[];
  initialSearch?: string | null;
  onSearchClear?: () => void;
  onViewReports?: (email: string) => void;
  reportFilterEmail?: string | null;
  onReportFilterClear?: () => void;
};

const formatName = (name: string) => name.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

const StudentsTab = ({ sessionToken, students, studentsLoading, fetchStudents, courses, initialSearch, onSearchClear, onViewReports, reportFilterEmail, onReportFilterClear }: Props) => {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [expandedStudent, setExpandedStudent] = useState<string | null>(null);
  const [approvingAll, setApprovingAll] = useState(false);
  const [innerTab, setInnerTab] = useState<string>("list");

  useEffect(() => {
    if (reportFilterEmail) setInnerTab("reports");
  }, [reportFilterEmail]);

  useEffect(() => {
    if (initialSearch) {
      setSearch(initialSearch);
      // Automatically expand if there's only one match
      const matching = students.filter(s => s.email.toLowerCase().includes(initialSearch.toLowerCase()));
      if (matching.length === 1) {
        setExpandedStudent(matching[0].user_id);
      }
    }
  }, [initialSearch, students]);

  const handleRevokeAccess = async (userId: string, courseId: string) => {
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "revoke_enrollment", sessionToken, userId, courseId },
      });
      if (res.data?.success) { toast({ title: "Access revoked" }); fetchStudents(); }
    } catch { toast({ title: "Failed to revoke access", variant: "destructive" }); }
  };

  const handleGrantAccess = async (userId: string, courseId: string) => {
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "grant_enrollment", sessionToken, userId, courseId },
      });
      if (res.data?.success) { toast({ title: "Access granted" }); fetchStudents(); }
    } catch { toast({ title: "Failed to grant access", variant: "destructive" }); }
  };

  const handleApproveEnrollment = async (enrollmentId: string) => {
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "approve_enrollment", sessionToken, enrollmentId },
      });
      if (res.data?.success) { toast({ title: "Enrollment approved" }); fetchStudents(); }
    } catch { toast({ title: "Failed to approve", variant: "destructive" }); }
  };

  const handleRejectEnrollment = async (enrollmentId: string) => {
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "reject_enrollment", sessionToken, enrollmentId },
      });
      if (res.data?.success) { toast({ title: "Enrollment rejected" }); fetchStudents(); }
    } catch { toast({ title: "Failed to reject", variant: "destructive" }); }
  };

  const handleApproveAllEnrollments = async (courseId?: string) => {
    const list = courseId
      ? filtered.filter((s) => s.enrollments.some((e) => e.course_id === courseId))
      : filtered;
    
    const pendingEnrollments: { id: string; studentName: string }[] = [];
    list.forEach((s) => {
      s.enrollments.forEach((e) => {
        if (e.status === "pending" && (!courseId || e.course_id === courseId)) {
          pendingEnrollments.push({ id: e.id, studentName: s.full_name });
        }
      });
    });

    if (pendingEnrollments.length === 0) {
      toast({ title: "No pending enrollments to approve" });
      return;
    }

    setApprovingAll(true);
    try {
      let successCount = 0;
      for (const enrollment of pendingEnrollments) {
        try {
          const res = await supabase.functions.invoke("manage-certificates", {
            body: { action: "approve_enrollment", sessionToken, enrollmentId: enrollment.id },
          });
          if (res.data?.success) successCount++;
        } catch (err) {
          console.error("Individual enrollment approval failed:", err);
        }
      }
      toast({ title: `Approved ${successCount}/${pendingEnrollments.length} enrollments` });
      fetchStudents();
    } catch (err) {
      console.error("Bulk approval error:", err);
      toast({ title: "Error approving all", variant: "destructive" });
    } finally { setApprovingAll(false); }
  };

  const filtered = students.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.full_name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q) || s.phone.includes(q);
  });

  const getPendingCount = (courseId?: string) => {
    const list = courseId
      ? filtered.filter((s) => s.enrollments.some((e) => e.course_id === courseId))
      : filtered;
    let count = 0;
    list.forEach((s) => {
      s.enrollments.forEach((e) => {
        if (e.status === "pending" && (!courseId || e.course_id === courseId)) count++;
      });
    });
    return count;
  };

  const renderStudentTable = (studentList: StudentInfo[], courseId?: string) => {
    const list = courseId
      ? studentList.filter((s) => s.enrollments.some((e) => e.course_id === courseId))
      : studentList;

    const pendingCount = getPendingCount(courseId);

    return (
      <div className="space-y-4">
        {pendingCount > 0 && (
          <div className="flex justify-end">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" disabled={approvingAll}>
                  {approvingAll ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCheck className="w-4 h-4 mr-1" />}
                  Approve All ({pendingCount})
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Approve all pending enrollments?</AlertDialogTitle>
                  <AlertDialogDescription>This will approve {pendingCount} pending enrollment request(s). Students will be notified.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => handleApproveAllEnrollments(courseId)}>Approve All</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
        {list.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">
            <Users className="w-10 h-10 mx-auto mb-3" /><p>No students found.</p>
          </CardContent></Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Full Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list.map((student) => {
                    const courseEnrollment = courseId
                      ? student.enrollments.find((e) => e.course_id === courseId)
                      : student.enrollments[0];
                    const courseProgress = courseId
                      ? student.progress.filter((p) => p.course_id === courseId)
                      : student.progress;

                    return (
                      <Fragment key={student.user_id}>
                        <TableRow className="cursor-pointer" onClick={() => setExpandedStudent(expandedStudent === student.user_id ? null : student.user_id)}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {expandedStudent === student.user_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              {formatName(student.full_name)}
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{student.email}</TableCell>
                          <TableCell>
                             {courseEnrollment ? (
                              <div className="flex items-center gap-1">
                                <Badge variant={courseEnrollment.status === "approved" ? "default" : courseEnrollment.status === "pending" ? "secondary" : "destructive"}>
                                  {courseEnrollment.status === "revoked" ? "Revoked (Security)" : courseEnrollment.status}
                                </Badge>
                                {courseEnrollment.status === "revoked" && (
                                  <ShieldAlert className="w-4 h-4 text-destructive animate-pulse" />
                                )}
                              </div>
                            ) : (
                              <Badge variant="outline">Not enrolled</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 min-w-[120px]">
                              <Progress value={student.progress_percent} className="h-2 flex-1" />
                              <span className="text-xs text-muted-foreground w-8">{student.progress_percent}%</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{new Date(student.created_at).toLocaleDateString()}</TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex gap-1 justify-end flex-wrap">
                              {courseEnrollment?.status === "pending" && (
                                <>
                                  <Button size="sm" variant="default" onClick={() => handleApproveEnrollment(courseEnrollment.id)} title="Approve">
                                    <CheckCircle className="w-3 h-3 mr-1" /> Approve
                                  </Button>
                                  <Button size="sm" variant="destructive" onClick={() => handleRejectEnrollment(courseEnrollment.id)} title="Reject">
                                    <XCircle className="w-3 h-3" />
                                  </Button>
                                </>
                              )}
                              {courseEnrollment?.status === "approved" && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button size="sm" variant="destructive"><UserX className="w-3 h-3 mr-1" /> Revoke</Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Revoke access?</AlertDialogTitle>
                                      <AlertDialogDescription>Remove {student.full_name} from {courseEnrollment.course_title}?</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleRevokeAccess(student.user_id, courseEnrollment.course_id)}>Revoke</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                              {(courseEnrollment?.status === "revoked" || courseEnrollment?.status === "rejected" || !courseEnrollment) && courseId && (
                                <Button size="sm" variant="outline" onClick={() => handleGrantAccess(student.user_id, courseId)}>
                                  <UserCheck className="w-3 h-3 mr-1" /> Grant Access
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        {expandedStudent === student.user_id && courseProgress.length > 0 && (
                          <TableRow>
                            <TableCell colSpan={6} className="bg-muted/30 p-4">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Lesson</TableHead>
                                    <TableHead>Video</TableHead>
                                    <TableHead>Exam</TableHead>
                                    <TableHead>Score</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {courseProgress.map((p) => (
                                    <TableRow key={p.lesson_id}>
                                      <TableCell className="font-medium">{p.lesson_title}</TableCell>
                                      <TableCell>
                                        <Badge variant={p.video_completed ? "default" : "secondary"}>
                                          {p.video_completed ? "Watched" : "Not watched"}
                                        </Badge>
                                      </TableCell>
                                      <TableCell>
                                        <Badge variant={p.exam_passed ? "default" : "secondary"}>
                                          {p.exam_passed ? "Passed" : "Not passed"}
                                        </Badge>
                                      </TableCell>
                                      <TableCell>{p.best_score}%</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Students");
    sheet.columns = [
      { header: "Full Name", key: "name", width: 25 },
      { header: "Email", key: "email", width: 30 },
      { header: "Phone", key: "phone", width: 18 },
      { header: "Joined", key: "joined", width: 15 },
      { header: "Status", key: "status", width: 12 },
      { header: "Progress", key: "progress", width: 12 },
    ];
    filtered.forEach((s) => {
      const enrollment = s.enrollments[0];
      sheet.addRow({
        name: formatName(s.full_name),
        email: s.email,
        phone: formatPhoneNumber(s.phone),
        joined: new Date(s.created_at).toLocaleDateString(),
        status: enrollment?.status || "Not enrolled",
        progress: `${s.progress_percent}%`,
      });
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `students_${new Date().toISOString().split("T")[0]}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <Tabs value={innerTab} onValueChange={setInnerTab}>
        <TabsList>
          <TabsTrigger value="list" className="flex items-center gap-1.5">
            <Users className="w-4 h-4" /> Students
          </TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-1.5">
            <ShieldAlert className="w-4 h-4" /> Reports
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Students</h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleExport} disabled={studentsLoading || filtered.length === 0}>
                <Download className="w-4 h-4 mr-1" /> Export
              </Button>
              <Button variant="outline" onClick={fetchStudents} disabled={studentsLoading}>
                {studentsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search students by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
              {(search || initialSearch) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-7 px-2"
                  onClick={() => {
                    setSearch("");
                    setExpandedStudent(null);
                    if (onSearchClear) onSearchClear();
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          {studentsLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
          ) : courses.length <= 1 ? (
            renderStudentTable(filtered, courses[0]?.id)
          ) : (
            <Tabs defaultValue={courses[0]?.id}>
              <TabsList>
                {courses.map((c) => (
                  <TabsTrigger key={c.id} value={c.id}>{c.title}</TabsTrigger>
                ))}
              </TabsList>
              {courses.map((c) => (
                <TabsContent key={c.id} value={c.id}>
                  {renderStudentTable(filtered, c.id)}
                </TabsContent>
              ))}
            </Tabs>
          )}
        </TabsContent>

        <TabsContent value="reports" className="mt-4">
          <ReportsTab
            sessionToken={sessionToken}
            filterEmail={reportFilterEmail}
            onFilterClear={onReportFilterClear}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StudentsTab;
